import React from 'react';
import PropTypes from 'prop-types';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';

/**
 * ProcessingProgress Component
 *
 * Displays batch processing progress and status
 *
 * @component
 */
const ProcessingProgress = ({ batchStatus = null, onCancel, onViewResults }) => {

  if (!batchStatus) return null;

  const { status, progress, processedRecords, totalRecords, batchId, message } = batchStatus;

  /**
   * Get status severity for Tag
   */
  const getStatusSeverity = () => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PROCESSING':
        return 'info';
      case 'FAILED':
        return 'danger';
      case 'CANCELLED':
        return 'warning';
      default:
        return 'info';
    }
  };

  /**
   * Get status icon
   */
  const getStatusIcon = () => {
    switch (status) {
      case 'COMPLETED':
        return 'pi-check-circle';
      case 'PROCESSING':
        return 'pi-spin pi-spinner';
      case 'FAILED':
        return 'pi-times-circle';
      case 'CANCELLED':
        return 'pi-ban';
      default:
        return 'pi-info-circle';
    }
  };

  /**
   * Calculate estimated time remaining
   */
  const getEstimatedTime = () => {
    if (status !== 'PROCESSING' || progress === 0) return 'Calculating...';

    const remainingProgress = 100 - progress;
    const estimatedSeconds = Math.ceil(remainingProgress * 2); // Rough estimate: 2 seconds per percent

    if (estimatedSeconds < 60) {
      return `~${estimatedSeconds} seconds`;
    }
    const minutes = Math.ceil(estimatedSeconds / 60);
    return `~${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  const progressPercentage = progress || 0;
  const isProcessing = status === 'PROCESSING';
  const isCompleted = status === 'COMPLETED';
  const isFailed = status === 'FAILED';
  const isCancelled = status === 'CANCELLED';

  return (
    <Card className="processing-progress-card">
      <div className="progress-header">
        <div className="progress-title">
          <h3>Batch Processing Status</h3>
          <Tag
            value={status}
            severity={getStatusSeverity()}
            icon={`pi ${getStatusIcon()}`}
          />
        </div>
        {batchId && (
          <div className="batch-id">
            <small>Batch ID: {batchId}</small>
          </div>
        )}
      </div>

      <div className="progress-content">
        <ProgressBar
          value={progressPercentage}
          showValue={true}
          className={`progress-bar ${status.toLowerCase()}`}
        />

        <div className="progress-stats">
          <div className="stat-item">
            <span className="stat-label">Processed Records:</span>
            <span className="stat-value">
              {processedRecords || 0} / {totalRecords || 0}
            </span>
          </div>

          {isProcessing && (
            <div className="stat-item">
              <span className="stat-label">Estimated Time:</span>
              <span className="stat-value">{getEstimatedTime()}</span>
            </div>
          )}

          {message && (
            <div className="stat-item full-width">
              <span className="stat-label">Message:</span>
              <span className="stat-value">{message}</span>
            </div>
          )}
        </div>
      </div>

      <div className="progress-actions">
        {isProcessing && (
          <Button
            label="Cancel Processing"
            icon="pi pi-times"
            className="p-button-danger p-button-outlined"
            onClick={() => onCancel(batchId)}
          />
        )}

        {isCompleted && (
          <Button
            label="View Results"
            icon="pi pi-eye"
            className="p-button-success"
            onClick={() => onViewResults(batchId)}
          />
        )}

        {(isFailed || isCancelled) && (
          <div className="error-message">
            <i className="pi pi-exclamation-triangle"></i>
            <span>
              {isFailed ? 'Processing failed. Please check the error and try again.' : 'Processing was cancelled.'}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
};

ProcessingProgress.propTypes = {
  batchStatus: PropTypes.shape({
    batchId: PropTypes.string,
    status: PropTypes.string,
    progress: PropTypes.number,
    processedRecords: PropTypes.number,
    totalRecords: PropTypes.number,
    message: PropTypes.string
  }),
  onCancel: PropTypes.func.isRequired,
  onViewResults: PropTypes.func.isRequired
};

export default ProcessingProgress;

